import { createStore, applyMiddleware, compose } from 'redux' // general redux packages
import { persistStore, persistReducer } from 'redux-persist' // persist packages
import thunk from 'redux-thunk' // redux middleware
import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
import rootReducer from 'redux/reducers' // project root reducer

// redux storybook integration https://github.com/frodare/addon-redux#register
import withReduxEnhancer from 'addon-redux/enhancer' // required for storybook

// dev tools
import { composeWithDevTools } from 'redux-devtools-extension'
import {
  __aok_test_system__,
  __dev__,
  __local_cdn__,
  __show_debug_output__,
  __storybook__,
} from 'settings/app/constants'
import exampleStoreStorybook from './exampleStoreStorybook'

import featureToggles from 'feature-toggles'

/**
 * config for persist reducers
 * @type {{storage: *, key: string}}
 */
export const persistConfig = {
  key: __dev__ ? 'navigatorAppDev' : 'navigatorApp',
  storage,
  blacklist: ['detailData'],
}

/**
 * reset store on initial app loading to avoid persisted storage error
 * those errors occurs if the the store structure has changed
 * Source: https://stackoverflow.com/questions/35622588/how-to-reset-the-state-of-a-redux-store
 *
 * @param state
 * @param action
 * @returns {CombinedState<unknown>}
 */
export const appReducer = (state, action) => {
  if (action.type === 'RESET_PREVIOUSLY_CACHE') {
    state = undefined
  }
  return rootReducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, appReducer)

/**
 * store middleware
 * @type {*[]}
 */
const middleware = [thunk]

/**
 * enhancer stack
 * @returns {(...args: any[]) => unknown}
 */
const createEnhancer = () => {
  const enhancers = []
  enhancers.push(withReduxEnhancer)
  return compose(...enhancers)
}

/**
 * createEnhancedStore
 * @returns {Store<unknown, Action<any>> & Store<S, A> & {dispatch: unknown}}
 * with or without redux dev tools
 */
export const createEnhancedStore = (initialState = {}) => {
  const exportWithReduxDevTools =
    __dev__ || __storybook__ || __aok_test_system__ || __local_cdn__

  // export with redux dev tools
  if (exportWithReduxDevTools) {
    const preLoadedState = __storybook__ ? exampleStoreStorybook : initialState

    return createStore(
      featureToggles.persistStore ? persistedReducer : appReducer,
      preLoadedState,
      composeWithDevTools({ trace: true, traceLimit: 25 })(
        applyMiddleware(...middleware),
        createEnhancer()
      )
    )
  }

  // export without redux dev tools
  return createStore(
    persistedReducer,
    initialState,
    applyMiddleware(...middleware)
  )
}

/**
 * export store config
 * @returns {{store: *, persistor: *}}
 */
export default () => {
  const store = createEnhancedStore()
  const persistor = persistStore(store)
  return { store, persistor }
}
